import type {
  DigestDTO,
  DigestStatus,
  ListResponse,
  PostDTO,
  PostListQuery,
  SessionUser,
  SystemStatusDTO,
  TargetDTO,
  UpdatePostRequest,
} from '@bb/shared';

export const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? 'GET',
    // Same-origin in dev thanks to the Vite proxy; explicit anyway so a
    // different deployment topology does not silently drop the session cookie.
    credentials: 'include',
    headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? `Request failed with status ${response.status}.`,
      error?.details,
    );
  }

  return payload as T;
}

function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export type TagWithCount = { id: string; name: string; color: string; postCount: number };

export type DigestSummary = {
  id: string;
  date: string;
  status: DigestStatus;
  headline: string | null;
  generatedAt: string | null;
  costMicros: number | null;
};

export type IngestionEventRow = {
  id: string;
  source: string;
  status: string;
  xPostId: string | null;
  xUserId: string | null;
  reason: string | null;
  error: string | null;
  durationMs: number | null;
  receivedAt: string;
};

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<{ user: SessionUser }>('/auth/login', {
        method: 'POST',
        body: { email, password },
      }),
    logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
    me: () => request<{ user: SessionUser }>('/auth/me'),
  },

  posts: {
    list: (query: PostListQuery, signal?: AbortSignal) =>
      request<ListResponse<PostDTO>>(
        `/posts${toQueryString(query as Record<string, unknown>)}`,
        signal ? { signal } : {},
      ),
    get: (id: string) => request<{ post: PostDTO }>(`/posts/${id}`),
    update: (id: string, patch: UpdatePostRequest) =>
      request<{ post: PostDTO }>(`/posts/${id}`, { method: 'PATCH', body: patch }),
    bulk: (ids: string[], patch: { isRead?: boolean; isPinned?: boolean }) =>
      request<{ updated: number }>('/posts/bulk', { method: 'POST', body: { ids, ...patch } }),
    remove: (id: string) => request<void>(`/posts/${id}`, { method: 'DELETE' }),
  },

  targets: {
    list: () => request<{ targets: TargetDTO[] }>('/targets'),
    create: (input: { handle: string; color?: string | null; notes?: string | null }) =>
      request<{ target: TargetDTO }>('/targets', { method: 'POST', body: input }),
    update: (id: string, patch: { isActive?: boolean; color?: string | null; notes?: string | null }) =>
      request<{ target: TargetDTO }>(`/targets/${id}`, { method: 'PATCH', body: patch }),
    remove: (id: string, confirmPostDeletion = false) =>
      request<void>(`/targets/${id}${confirmPostDeletion ? '?confirmPostDeletion=true' : ''}`, {
        method: 'DELETE',
      }),
  },

  tags: {
    list: () => request<{ tags: TagWithCount[] }>('/tags'),
    create: (name: string, color?: string) =>
      request<{ tag: { id: string; name: string; color: string } }>('/tags', {
        method: 'POST',
        body: color ? { name, color } : { name },
      }),
    remove: (id: string) => request<void>(`/tags/${id}`, { method: 'DELETE' }),
  },

  digests: {
    list: () =>
      request<{ digests: DigestSummary[]; today: string; enabled: boolean }>('/digests'),
    get: (date: string) => request<{ digest: DigestDTO }>(`/digests/${date}`),
    generate: (date?: string, force = false) =>
      request<{ digest: DigestDTO }>('/digests/generate', {
        method: 'POST',
        body: { ...(date ? { date } : {}), force },
      }),
    getPrompt: () =>
      request<{ prompt: string; defaultPrompt: string }>('/digests/prompt'),
    setPrompt: (prompt: string) =>
      request<{ prompt: string }>('/digests/prompt', { method: 'PUT', body: { prompt } }),
  },

  system: {
    status: () => request<SystemStatusDTO>('/system/status'),
    ingestionEvents: (limit = 50) =>
      request<{ events: IngestionEventRow[] }>(`/system/ingestion-events?limit=${limit}`),
  },
};
